import { word } from "@/modules/core/utils/i18n"
import {
  ScoreResultType,
  ScoreType,
  type ScoreResultDraftType,
  type ScoreSetConfigurationFormikType,
} from "@/modules/scores/scoreTypes"
import { getScoreResultTypeCaption, getScoreResultTypesArray, getScoreTypesArray } from "@/modules/scores/scoreUtils"

export type ExerciseScoreFormModel = {
  canAddScoreSetRow: boolean
  canValidateScoreForm: boolean
  canSetScoreResultType: boolean
  canRemoveScoreSetRow: boolean
  canSetMediaRequirement: boolean
  availableScoreTypes: { label: string; value: ScoreType }[]
  availableScoreResultTypes: { label: string; value: ScoreResultDraftType }[]
  scoreResultText: string | null
}

const MAX_SCORE_SET_ROWS = 99

export function createExerciseScoreFormModel(
  scoreSetConfigurations: ScoreSetConfigurationFormikType[] | null,
  scoringOption: ScoreResultDraftType | null,
): ExerciseScoreFormModel {
  return {
    canAddScoreSetRow:
      scoreSetConfigurations !== null
        ? scoreSetConfigurations.length < MAX_SCORE_SET_ROWS
          ? !scoreSetConfigurations.some((scoreSet) => scoreSet.scoreType === ScoreType.TEXT_FROM_ATHLETE)
          : false
        : true,
    canValidateScoreForm:
      scoreSetConfigurations && scoreSetConfigurations.length > 0
        ? scoreSetConfigurations.length > 1
          ? scoringOption !== null
          : true
        : false,
    canSetScoreResultType: (scoreSetConfigurations?.length ?? 0) > 1,
    canRemoveScoreSetRow: (scoreSetConfigurations?.length ?? 0) > 1,
    canSetMediaRequirement: (scoreSetConfigurations?.length ?? 0) >= 1,
    /**
     * availableScoreTypes:
     * if scoreSetConfigurations has one row and the score type is TEXT_FROM_ATHLETE, no other score types are available
     * if scoreSetConfigurations has more than one row, only score types that are not TEXT_FROM_ATHLETE are available
     */
    availableScoreTypes: getScoreTypesArray().filter(
      (scoreType) =>
        scoreType.value !== ScoreType.NO_SCORE &&
        ((scoreSetConfigurations?.length ?? 0) > 1 ? scoreType.value !== ScoreType.TEXT_FROM_ATHLETE : true),
    ),
    /**
     * availableScoreResultTypes:
     * if scoreSetConfigurations has one row, no score result type is available
     * if scoreSetConfigurations has more than one row, and if all scoreSetConfigurations have the same score type, all score result types are available
     * if scoreSetConfigurations has more than one row, and if all scoreSetConfigurations have FOR_TIME, ScoreResultDraftType.AVERAGE isn't available
     * if scoreSetConfigurations has more than one row, and if scoreSetConfigurations have different score types, only ScoreResultDraftType.SET_N_FOR_SCOREBOARD is available
     * Replace SET_N_FOR_SCOREBOARD row by SET_N_FOR_SCOREBOARD with the index of the score set for each score set
     * **/
    availableScoreResultTypes:
      scoreSetConfigurations !== null && scoreSetConfigurations.length > 1
        ? getScoreResultTypesArray()
            .filter(
              (scoreResultType) =>
                (scoreSetConfigurations[0]?.scoreType === ScoreType.TIME
                  ? scoreResultType.value !== ScoreResultType.AVERAGE
                  : true) && // exclude AVERAGE because it's not available if all scoreSetConfigurations have FOR_TIME
                scoreSetConfigurations.every((scoreSet) => scoreSet.scoreType === scoreSetConfigurations[0]?.scoreType), // all scoreSetConfigurations have the same score type
            )
            .concat(
              scoreSetConfigurations.map((_scoreSet, index) => ({
                label: word("global.scoreResult.setNForScoreboard.label", { index: index + 1 }),
                value: `SET_N_FOR_SCOREBOARD_${index}`,
              })), // add SET_N_FOR_SCOREBOARD for each score set
            )
        : [],
    scoreResultText: scoringOption ? getScoreResultTypeCaption(scoringOption) : null,
  }
}
